import axios from 'axios'
import Cookies from 'js-cookie'

// Configuração base da API
const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
})

// Interceptor de requisição para adicionar token
api.interceptors.request.use(
  (config) => {
    const token = Cookies.get('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Interceptor de resposta para lidar com erros
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    
    // Se erro 401 e não é uma tentativa de refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      
      const refreshToken = Cookies.get('refresh_token')
      if (refreshToken) {
        try {
          // Tentar renovar token
          const response = await axios.post('/api/auth/refresh/', {
            refresh: refreshToken
          })
          
          const { access } = response.data
          
          // Atualizar tokens
          Cookies.set('access_token', access, { expires: 1 })
          api.defaults.headers.common['Authorization'] = `Bearer ${access}`
          originalRequest.headers.Authorization = `Bearer ${access}`
          
          // Repetir requisição original
          return api(originalRequest)
          
        } catch (refreshError) {
          // Refresh falhou, redirecionar para login
          Cookies.remove('access_token')
          Cookies.remove('refresh_token')
          Cookies.remove('user_data')
          
          if (window.location.pathname !== '/login') {
            window.location.href = '/login'
          }
        }
      }
    }
    
    return Promise.reject(error)
  }
)

export default api

// ==========================================
// SERVIÇOS DE AUTENTICAÇÃO
// ==========================================
export const authAPI = {
  login: (credentials) => api.post('/auth/login/', credentials),
  register: (userData) => api.post('/auth/register/', userData),
  logout: () => api.post('/auth/logout/'),
  refresh: (refreshToken) => api.post('/auth/refresh/', { refresh: refreshToken }),
  profile: () => api.get('/auth/profile/'),
  updateProfile: (data) => api.patch('/auth/profile/', data),
  changePassword: (data) => api.post('/auth/change-password/', data),
}

// ==========================================
// TUNNEL API COM VALIDAÇÃO DE PORTAS APRIMORADA
// ==========================================
export const tunnelAPI = {
  // CRUD básico
  list: (params) => api.get('/tunnels/', { params }),
  get: (id) => api.get(`/tunnels/${id}/`),
  
  // Criar túnel com validação de portas
  async create(data) {
    try {
      console.log('📝 Enviando dados para criação de túnel:', data)
      
      // Validações do frontend antes de enviar
      await this.validateTunnelData(data)
      
      const response = await api.post('/tunnels/', data)
      console.log('✅ Túnel criado com sucesso:', response.data)
      return response
    } catch (error) {
      console.error('❌ Erro ao criar túnel:', error)
      
      // Tratar erros específicos de validação
      if (error.response?.status === 400) {
        const errorData = error.response.data
        
        // Reformatar erros de validação para melhor UX
        if (errorData.local_port && Array.isArray(errorData.local_port)) {
          errorData.local_port = errorData.local_port[0]
        }
        if (errorData.remote_port && Array.isArray(errorData.remote_port)) {
          errorData.remote_port = errorData.remote_port[0]
        }
        if (errorData.name && Array.isArray(errorData.name)) {
          errorData.name = errorData.name[0]
        }
        
        error.response.data = errorData
      }
      
      throw error
    }
  },

  // Atualizar túnel com validação
  async update(id, data) {
    try {
      console.log(`📝 Atualizando túnel ${id}:`, data)
      
      // Validações do frontend
      await this.validateTunnelData(data, id)
      
      const response = await api.patch(`/tunnels/${id}/`, data)
      console.log('✅ Túnel atualizado com sucesso:', response.data)
      return response
    } catch (error) {
      console.error(`❌ Erro ao atualizar túnel ${id}:`, error)
      throw error
    }
  },

  async getProvidersData(type = 'all', activeOnly = true) {
    try {
      const response = await api.get('/tunnels/providers_data/', {
        params: {
          type: type,
          active_only: activeOnly
        }
      })
      return response
    } catch (error) {
      console.error('Erro ao buscar dados de providers:', error)
      return {
        data: {
          success: false,
          data: {
            providers: [],
            cellphone_providers: [],
            sim_cards: []
          }
        }
      }
    }
  },

  // Limpar cache de providers
  async clearProvidersCache() {
    try {
      const response = await api.post('/tunnels/clear_providers_cache/')
      return response
    } catch (error) {
      console.error('Erro ao limpar cache de providers:', error)
      throw error
    }
  },

  // Buscar apenas providers
  async getProviders(activeOnly = true) {
    const response = await this.getProvidersData('providers', activeOnly)
    return response.data.data.providers || []
  },

  // Buscar apenas operadoras
  async getCellphoneProviders(activeOnly = true) {
    const response = await this.getProvidersData('cellphone_providers', activeOnly)
    return response.data.data.cellphone_providers || []
  },

  // Buscar apenas SIM cards
  async getSimCards(activeOnly = true) {
    const response = await this.getProvidersData('sim_cards', activeOnly)
    return response.data.data.sim_cards || []
  },

  delete: (id) => api.delete(`/tunnels/${id}/`),
  
  // Ações de controle
  start: (id) => api.post(`/tunnels/${id}/start/`),
  stop: (id) => api.post(`/tunnels/${id}/stop/`),
  restart: (id) => api.post(`/tunnels/${id}/restart/`),
  
  // Ações em lote
  startAll: () => api.post('/tunnels/start_all/'),
  stopAll: () => api.post('/tunnels/stop_all/'),
  
  // Dados e estatísticas
  stats: () => api.get('/tunnels/stats/'),
  connections: (id) => api.get(`/tunnels/${id}/connections/`),
  data: (id, params) => api.get(`/tunnels/${id}/data/`, { params }),
  dataExport: (id) => api.get(`/tunnels/${id}/data_export/`),
  cleanupData: (id, days) => api.post(`/tunnels/${id}/cleanup_data/`, { days }),
  toggleDataSaving: (id) => api.post(`/tunnels/${id}/toggle_data_saving/`),

  // ==========================================
  // NOVAS FUNCIONALIDADES DE VALIDAÇÃO DE PORTAS
  // ==========================================

  // Verificar disponibilidade de porta específica
  async checkPortAvailability(port, excludeTunnelId = null) {
    try {
      const params = { port }
      if (excludeTunnelId) {
        params.exclude_tunnel_id = excludeTunnelId
      }
      
      const response = await api.get('/tunnels/check-port/', { params })
      return response
    } catch (error) {
      console.error(`❌ Erro ao verificar porta ${port}:`, error)
      return {
        data: {
          available: false,
          message: 'Erro ao verificar disponibilidade da porta',
          error: error.message,
          conflicting_tunnel: null,
          system_usage: false
        }
      }
    }
  },

  // Obter lista de portas disponíveis
  async getAvailablePorts(startPort = null, endPort = null, limit = 100) {
    try {
      const params = { limit }
      if (startPort) params.start_port = startPort
      if (endPort) params.end_port = endPort
      
      const response = await api.get('/tunnels/available-ports/', { params })
      return response
    } catch (error) {
      console.error('❌ Erro ao obter portas disponíveis:', error)
      return {
        data: {
          ports: [],
          message: 'Erro ao obter portas disponíveis',
          error: error.message
        }
      }
    }
  },

  // Obter próxima porta disponível
  async getNextAvailablePort(startFrom = 8000) {
    try {
      const response = await this.getAvailablePorts(startFrom, startFrom + 1000, 1)
      const ports = response.data.ports || []
      return ports.length > 0 ? ports[0] : null
    } catch (error) {
      console.error('❌ Erro ao obter próxima porta disponível:', error)
      return null
    }
  },

  // Sugerir portas alternativas baseadas na porta preferida
  async suggestAlternativePorts(preferredPort, count = 5) {
    try {
      const response = await api.get('/tunnels/suggest-ports/', {
        params: { preferred: preferredPort, count }
      })
      return response.data.suggestions || []
    } catch (error) {
      console.error('Erro ao sugerir portas alternativas:', error)
      
      // Fallback: gerar sugestões localmente
      const suggestions = []
      let currentPort = Math.max(preferredPort + 1, 8000)
      
      // Gerar algumas sugestões baseadas na porta preferida
      for (let i = 0; i < count && currentPort <= 65535; i++) {
        suggestions.push(currentPort)
        currentPort++
      }
      
      return suggestions
    }
  },

  // ==========================================
  // VALIDAÇÕES DO FRONTEND
  // ==========================================

  async validateTunnelData(tunnelData, excludeTunnelId = null) {
    const errors = {}
    
    // Validação básica de dados obrigatórios
    if (!tunnelData.name || !tunnelData.name.trim()) {
      errors.name = 'Nome é obrigatório'
    }
    
    if (!tunnelData.local_port) {
      errors.local_port = 'Porta local é obrigatória'
    }
    
    if (!tunnelData.remote_port) {
      errors.remote_port = 'Porta remota é obrigatória'
    }
    
    // Validação de range de portas
    if (tunnelData.local_port && (tunnelData.local_port < 1 || tunnelData.local_port > 65535)) {
      errors.local_port = 'Porta local deve estar entre 1 e 65535'
    }
    
    if (tunnelData.remote_port && (tunnelData.remote_port < 1 || tunnelData.remote_port > 65535)) {
      errors.remote_port = 'Porta remota deve estar entre 1 e 65535'
    }
    
    // Validação de portas iguais
    if (tunnelData.local_port && tunnelData.remote_port && 
        tunnelData.local_port === tunnelData.remote_port) {
      errors.local_port = 'Porta local e remota não podem ser iguais'
      errors.remote_port = 'Porta local e remota não podem ser iguais'
    }
    
    // Se há erros básicos, não prosseguir com validações de rede
    if (Object.keys(errors).length > 0) {
      throw { 
        response: { 
          status: 400, 
          data: errors 
        },
        message: 'Dados de túnel inválidos'
      }
    }
    
    // Validações assíncronas de disponibilidade de portas
    const validationPromises = []
    
    if (tunnelData.local_port) {
      validationPromises.push(
        this.checkPortAvailability(tunnelData.local_port, excludeTunnelId)
          .then(response => {
            if (!response.data.available) {
              const errorMsg = this.formatPortError(response.data, tunnelData.local_port, 'local')
              errors.local_port = errorMsg
            }
          })
          .catch(error => {
            console.warn('Erro ao validar porta local:', error)
            // Não bloquear criação por erro de validação de rede
          })
      )
    }
    
    if (tunnelData.remote_port && tunnelData.remote_port !== tunnelData.local_port) {
      validationPromises.push(
        this.checkPortAvailability(tunnelData.remote_port, excludeTunnelId)
          .then(response => {
            if (!response.data.available) {
              const errorMsg = this.formatPortError(response.data, tunnelData.remote_port, 'remota')
              errors.remote_port = errorMsg
            }
          })
          .catch(error => {
            console.warn('Erro ao validar porta remota:', error)
            // Não bloquear criação por erro de validação de rede
          })
      )
    }
    
    // Aguardar todas as validações
    await Promise.all(validationPromises)
    
    // Se há erros de validação, lançar exceção
    if (Object.keys(errors).length > 0) {
      throw { 
        response: { 
          status: 400, 
          data: errors 
        },
        message: 'Portas não disponíveis'
      }
    }
    
    return true
  },

  // Formatar mensagem de erro de porta de forma mais amigável
  formatPortError(portData, port, portType) {
    if (portData.conflicting_tunnel) {
      return `Porta ${port} já está sendo usada pelo túnel "${portData.conflicting_tunnel.name}"`
    }
    
    if (portData.system_usage) {
      return `Porta ${port} já está sendo usada pelo sistema`
    }
    
    return portData.message || `Porta ${portType} ${port} não está disponível`
  },

  // Validar apenas uma porta específica (útil para validação em tempo real)
  async validateSinglePort(port, type = 'local', excludeTunnelId = null) {
    try {
      if (!port || port < 1 || port > 65535) {
        return {
          valid: false,
          message: `Porta ${type} deve estar entre 1 e 65535`
        }
      }
      
      const response = await this.checkPortAvailability(port, excludeTunnelId)
      
      return {
        valid: response.data.available,
        message: response.data.available ? 'Porta disponível' : this.formatPortError(response.data, port, type),
        details: response.data
      }
    } catch (error) {
      console.error(`Erro ao validar porta ${type} ${port}:`, error)
      return {
        valid: false,
        message: 'Erro ao verificar disponibilidade da porta'
      }
    }
  }
}

// ==========================================
// LOG API
// ==========================================
export const logAPI = {
  list: (params) => api.get('/logs/', { params }),
  stats: () => api.get('/logs/stats/'),
  recent: (params) => api.get('/logs/recent/', { params }),
  export: (params) => api.get('/logs/export/', { params }),
}

// ==========================================
// DATA API
// ==========================================
export const dataAPI = {
  list: (params) => api.get('/tunnels/data/list/', { params }),
  get: (id) => api.get(`/tunnels/data/${id}/`),
  content: (id) => api.get(`/tunnels/data/${id}/content/`),
  download: (id) => api.get(`/tunnels/data/${id}/download/`),
  stats: () => api.get('/tunnels/data/stats/'),
  export: (params) => api.get('/tunnels/data/export/', { params }),
  cleanup: (params) => api.post('/tunnels/data/cleanup/', params),
}

// ==========================================
// USER API
// ==========================================
export const userAPI = {
  list: (params) => api.get('/auth/users/', { params }),
  create: (data) => api.post('/auth/users/create/', data),
  get: (id) => api.get(`/auth/users/${id}/`),
  update: (id, data) => api.patch(`/auth/users/${id}/`, data),
  delete: (id) => api.delete(`/auth/users/${id}/`),
  toggleStatus: (id, is_active) => api.patch(`/auth/users/${id}/`, { is_active }),
}